import { useEffect, useState } from "react";
import { useReducedMotion } from "framer-motion";
import { cn } from "@/lib/utils";

const IMAGES = [1, 2, 3, 4, 5].map((i) => `/login-bg/bg-${i}.webp`);

/** Brain made of circuit lines; navy→teal gradient with a soft glow pulse. */
export const BrainLogo = ({ className }) => (
  <svg viewBox="0 0 120 120" className={cn("brain-logo", className)} role="img" aria-label="Logo" data-testid="brain-logo">
    <defs>
      <linearGradient id="bl-g" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#5EEAD4" /><stop offset="0.55" stopColor="#14B8A6" /><stop offset="1" stopColor="#1E3A8A" /></linearGradient>
      <radialGradient id="bl-bg" cx="0.5" cy="0.5" r="0.5"><stop offset="0" stopColor="#0F2A4A" /><stop offset="1" stopColor="#0B1B33" /></radialGradient>
      <filter id="bl-glow" x="-40%" y="-40%" width="180%" height="180%"><feGaussianBlur stdDeviation="2.2" result="b" /><feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
    </defs>
    <circle cx="60" cy="60" r="56" fill="url(#bl-bg)" stroke="url(#bl-g)" strokeOpacity="0.5" strokeWidth="1.5" />
    <g fill="none" stroke="url(#bl-g)" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" filter="url(#bl-glow)">
      <path d="M57 28c-6-5-17-4-21 3-7 0-12 7-10 14-6 4-6 13 0 17-3 7 2 15 10 15 3 7 13 10 21 5" />
      <path d="M63 28c6-5 17-4 21 3 7 0 12 7 10 14 6 4 6 13 0 17 3 7-2 15-10 15-3 7-13 10-21 5" />
      <path d="M57 28v54M63 28v54" strokeOpacity="0.55" />
      <path d="M36 45h10l6 6M30 60h14l5-5M38 76l8-8h7M84 45H74l-6 6M90 60H76l-5-5M82 76l-8-8h-7" strokeWidth="2" />
    </g>
    <g fill="#5EEAD4" filter="url(#bl-glow)">
      {[[52, 51], [49, 55], [53, 68], [68, 51], [71, 55], [67, 68], [36, 45], [30, 60], [38, 76], [84, 45], [90, 60], [82, 76]].map(([x, y]) => <circle key={`${x}-${y}`} cx={x} cy={y} r="2.4" />)}
    </g>
  </svg>
);

/** Full-screen rotating factory/technology backdrop with a navy overlay. */
export const LoginBackdrop = () => {
  const reduce = useReducedMotion();
  const [i, setI] = useState(0);
  useEffect(() => {
    if (reduce) return undefined;
    const t = setInterval(() => setI((n) => (n + 1) % IMAGES.length), 6000);
    return () => clearInterval(t);
  }, [reduce]);
  return (
    <div className="fixed inset-0 -z-10 overflow-hidden bg-[#0B1B33]" aria-hidden="true" data-testid="login-backdrop">
      {IMAGES.map((src, n) => (
        <img key={src} src={src} alt="" loading={n === 0 ? "eager" : "lazy"} decoding="async"
          className={cn("absolute inset-0 h-full w-full object-cover transition-opacity duration-[1800ms] ease-in-out", n === i ? "opacity-100" : "opacity-0", !reduce && n === i && "login-kenburns")} />
      ))}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0B1B33]/80 via-[#0B1B33]/65 to-[#0B1B33]/95" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(11,27,51,0.55)_70%)]" />
    </div>
  );
};

export const glassCard = "w-full rounded-2xl border border-white/15 bg-white/10 p-6 text-white shadow-2xl shadow-black/30 backdrop-blur-xl sm:p-8";
export const glassInput = "h-12 border-white/20 bg-white/90 text-base text-slate-900 placeholder:text-slate-400";
